import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Coins, ArrowUpRight, ArrowDownLeft, Plus, Send, 
  History, RefreshCw, Wallet, TrendingUp, Gamepad2,
  ArrowRight, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const AGPWallet = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Purchase form
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [purchaseMode, setPurchaseMode] = useState('cash_only');
  const [purchasing, setPurchasing] = useState(false);
  
  // Transfer form
  const [transferRecipient, setTransferRecipient] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferring, setTransferring] = useState(false);
  
  // User wallets (Cash, Commission)
  const [userWallets, setUserWallets] = useState({ cash: 0, commission: 0 });

  useEffect(() => {
    fetchWallet();
    fetchTransactions();
    fetchUserWallets();
  }, []);

  const fetchWallet = async () => {
    try {
      const res = await api.get('/agp/wallet');
      setWallet(res.data.wallet);
    } catch (error) {
      console.error('Failed to fetch AGP wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await api.get('/agp/transactions?limit=20');
      setTransactions(res.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const fetchUserWallets = async () => {
    try {
      const res = await api.get('/user/profile');
      if (res.data.user) {
        setUserWallets({
          cash: res.data.user.cash_wallet || 0,
          commission: res.data.user.commission_wallet || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch user wallets:', error);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    const amount = parseFloat(purchaseAmount);
    
    if (!amount || amount <= 0) {
      toast({ title: "Error", description: "Enter a valid amount", variant: "destructive" });
      return;
    }

    setPurchasing(true);
    try {
      const res = await api.post('/agp/purchase', {
        amount,
        mode: purchaseMode
      });
      
      toast({
        title: "Purchase Successful!",
        description: `You received ${res.data.amount_purchased} AGP`,
      });
      
      setPurchaseAmount('');
      fetchWallet();
      fetchTransactions();
      fetchUserWallets();
    } catch (error) {
      toast({
        title: "Purchase Failed",
        description: error.response?.data?.detail || "Something went wrong",
        variant: "destructive"
      });
    } finally {
      setPurchasing(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    const amount = parseFloat(transferAmount);
    
    if (!transferRecipient.trim()) {
      toast({ title: "Error", description: "Enter recipient username/email/mobile", variant: "destructive" });
      return;
    }
    
    if (!amount || amount < 10) {
      toast({ title: "Error", description: "Minimum transfer is 10 AGP", variant: "destructive" });
      return;
    }

    setTransferring(true);
    try {
      const res = await api.post('/agp/transfer', {
        recipient_identifier: transferRecipient,
        amount
      });
      
      toast({
        title: "Transfer Successful!",
        description: `Sent ${res.data.amount_received} AGP to @${res.data.recipient_username}`,
      });
      
      setTransferRecipient('');
      setTransferAmount('');
      fetchWallet();
      fetchTransactions();
    } catch (error) {
      toast({
        title: "Transfer Failed",
        description: error.response?.data?.detail || "Something went wrong",
        variant: "destructive"
      });
    } finally {
      setTransferring(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase': return <Plus className="w-4 h-4 text-green-400" />;
      case 'reward': return <TrendingUp className="w-4 h-4 text-yellow-400" />;
      case 'game_entry': return <Gamepad2 className="w-4 h-4 text-red-400" />;
      case 'p2p_send': return <ArrowUpRight className="w-4 h-4 text-red-400" />;
      case 'p2p_receive': return <ArrowDownLeft className="w-4 h-4 text-green-400" />;
      case 'commission': return <Coins className="w-4 h-4 text-purple-400" />;
      default: return <Wallet className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="agp-wallet-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Coins className="w-7 h-7 text-yellow-500" />
              AGP Wallet
            </h1>
            <p className="text-muted-foreground text-sm">Anant Gaming Points</p>
          </div>
          <Button onClick={() => navigate('/games')} className="gap-2">
            <Gamepad2 className="w-4 h-4" />
            Play Games
          </Button>
        </div>

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Available Balance</p>
              <h2 className="text-5xl font-bold text-yellow-400" data-testid="agp-balance">
                {wallet?.balance?.toFixed(2) || '0.00'} AGP
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                ≈ ₹{wallet?.balance?.toFixed(2) || '0.00'}
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Purchased</p>
                <p className="font-semibold text-green-400">{wallet?.total_purchased?.toFixed(0) || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Earned</p>
                <p className="font-semibold text-yellow-400">{wallet?.total_earned?.toFixed(0) || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Games Won</p>
                <p className="font-semibold text-purple-400">{wallet?.games_won || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Games Played</p>
                <p className="font-semibold">{wallet?.games_played || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {['overview', 'purchase', 'transfer', 'history'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(tab)}
              className="capitalize"
            >
              {tab}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-4">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full justify-between" 
                  variant="outline"
                  onClick={() => setActiveTab('purchase')}
                >
                  <span className="flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Buy AGP
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  className="w-full justify-between" 
                  variant="outline"
                  onClick={() => setActiveTab('transfer')}
                >
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" /> Transfer AGP
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  className="w-full justify-between" 
                  variant="outline"
                  onClick={() => navigate('/games')}
                >
                  <span className="flex items-center gap-2">
                    <Gamepad2 className="w-4 h-4" /> Play & Earn
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Your Wallets */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Wallets</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-green-500/10 rounded-lg">
                  <span className="text-sm">Cash Wallet</span>
                  <span className="font-semibold text-green-400">₹{userWallets.cash.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-purple-500/10 rounded-lg">
                  <span className="text-sm">Commission Wallet</span>
                  <span className="font-semibold text-purple-400">₹{userWallets.commission.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-yellow-500/10 rounded-lg">
                  <span className="text-sm">AGP Wallet</span>
                  <span className="font-semibold text-yellow-400">{wallet?.balance?.toFixed(2) || 0} AGP</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'purchase' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" /> Purchase AGP
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePurchase} className="space-y-4">
                <div className="p-3 bg-blue-500/10 rounded-lg flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-400 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    1 AGP = ₹1. AGP cannot be converted back to Cash.
                  </p>
                </div>

                <div>
                  <Label>Amount (AGP)</Label>
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={purchaseAmount}
                    onChange={(e) => setPurchaseAmount(e.target.value)}
                    min="1"
                    max="100000"
                    data-testid="purchase-amount-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Cost: ₹{purchaseAmount || 0}
                  </p>
                </div>

                <div>
                  <Label>Payment Mode</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setPurchaseMode('cash_only')}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        purchaseMode === 'cash_only' 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <p className="font-medium">100% Cash</p>
                      <p className="text-xs text-muted-foreground">Pay from Cash Wallet</p>
                      <p className="text-xs text-green-400 mt-1">Available: ₹{userWallets.cash.toFixed(2)}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPurchaseMode('hybrid')}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        purchaseMode === 'hybrid' 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <p className="font-medium">50/50 Hybrid</p>
                      <p className="text-xs text-muted-foreground">50% Cash + 50% Commission</p>
                      <p className="text-xs text-purple-400 mt-1">Commission: ₹{userWallets.commission.toFixed(2)}</p>
                    </button>
                  </div>
                </div>

                {purchaseMode === 'hybrid' && purchaseAmount && (
                  <div className="p-3 bg-muted rounded-lg text-sm">
                    <p>Cash: ₹{(parseFloat(purchaseAmount) / 2).toFixed(2)}</p>
                    <p>Commission: ₹{(parseFloat(purchaseAmount) / 2).toFixed(2)}</p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={purchasing || !purchaseAmount}
                  data-testid="purchase-btn"
                >
                  {purchasing ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Purchase {purchaseAmount || 0} AGP
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {activeTab === 'transfer' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" /> Transfer AGP
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTransfer} className="space-y-4">
                <div className="p-3 bg-orange-500/10 rounded-lg flex items-start gap-2">
                  <Info className="w-4 h-4 text-orange-400 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    3% service fee applies. Minimum transfer: 10 AGP.
                  </p>
                </div>

                <div>
                  <Label>Recipient (Username / Email / Mobile)</Label>
                  <Input
                    type="text"
                    placeholder="Enter recipient"
                    value={transferRecipient}
                    onChange={(e) => setTransferRecipient(e.target.value)}
                    data-testid="transfer-recipient-input"
                  />
                </div>

                <div>
                  <Label>Amount (AGP)</Label>
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(e.target.value)}
                    min="10"
                    data-testid="transfer-amount-input"
                  />
                  {transferAmount && parseFloat(transferAmount) >= 10 && (
                    <div className="text-xs mt-1 space-y-1">
                      <p className="text-muted-foreground">
                        Service Fee (3%): {(parseFloat(transferAmount) * 0.03).toFixed(2)} AGP
                      </p>
                      <p className="text-green-400">
                        Recipient gets: {(parseFloat(transferAmount) * 0.97).toFixed(2)} AGP
                      </p>
                    </div>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={transferring || !transferRecipient || !transferAmount}
                  data-testid="transfer-btn"
                >
                  {transferring ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Transfer AGP
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {activeTab === 'history' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" /> Transaction History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No transactions yet
                </p>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-background rounded-full">
                          {getTransactionIcon(tx.type)}
                        </div>
                        <div>
                          <p className="font-medium text-sm capitalize">
                            {tx.type?.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${
                          tx.amount > 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {tx.amount > 0 ? '+' : ''}{tx.amount?.toFixed(2)} AGP
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Bal: {tx.balance_after?.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AGPWallet;
